import { useState, useEffect, useRef } from 'react';
import { API_URL } from '../config.js';
import {
  Lock, Mail, User, Eye, EyeOff, ArrowRight, Shield, Zap, Ticket,
  CheckCircle, XCircle, AlertCircle, Cpu
} from 'lucide-react';
import authService from '../services/auth';
import { APP_VERSION } from '../version';

/* ═══ Interactive Particle Mesh ═══ */
function ParticleMesh() {
  const canvasRef = useRef(null);
  const mouse = useRef({ x: -9999, y: -9999 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let w, h, animId;

    const resize = () => { w = canvas.width = window.innerWidth; h = canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);

    const onMove = (e) => { mouse.current = { x: e.clientX, y: e.clientY }; };
    window.addEventListener('mousemove', onMove);

    const N = 70;
    const pts = Array.from({ length: N }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.35,
      vy: (Math.random() - 0.5) * 0.35,
      r: Math.random() * 1.5 + 0.5,
      isPurple: Math.random() > 0.5
    }));

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      const mx = mouse.current.x, my = mouse.current.y;

      // Update positions
      for (const p of pts) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > w) p.vx *= -1;
        if (p.y < 0 || p.y > h) p.vy *= -1;

        // Mouse repulsion
        const dx = p.x - mx, dy = p.y - my;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 160 && dist > 0) {
          const force = (160 - dist) / 160 * 0.8;
          p.x += (dx / dist) * force * 3;
          p.y += (dy / dist) * force * 3;
        }
      }

      // Draw connections
      for (let i = 0; i < N; i++) {
        for (let j = i + 1; j < N; j++) {
          const dx = pts[i].x - pts[j].x;
          const dy = pts[i].y - pts[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 130) {
            const alpha = 0.08 * (1 - dist / 130);
            ctx.beginPath();
            ctx.strokeStyle = `rgba(139,92,246,${alpha})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.stroke();
          }
        }
      }

      // Draw dots
      for (const p of pts) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.isPurple ? 'rgba(139,92,246,0.35)' : 'rgba(34,211,238,0.25)';
        ctx.fill();
      }

      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
    };
  }, []);

  return <canvas ref={canvasRef} style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0 }} />;
}


export default function AuthPage({ onAuth }) {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ email: '', password: '', name: '', invitationCode: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [invitationStatus, setInvitationStatus] = useState(null);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);
  const [checkingSetup, setCheckingSetup] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [regToken, setRegToken] = useState(null); // from share link

  useEffect(() => { requestAnimationFrame(() => setMounted(true)); }, []);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/auth/setup-status`)
      .then(r => r.json())
      .then(data => { if (data.needsSetup) setMode('setup'); })
      .catch(() => {})
      .finally(() => setCheckingSetup(false));
  }, []);

  useEffect(() => {
    // Guard against React StrictMode double-mount
    let cancelled = false;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const code = params.get('code');
    if (token) {
      setMode('register');
      resolveShareToken(token, cancelled);
    } else if (code) {
      setForm(prev => ({ ...prev, invitationCode: code }));
      setMode('register');
      verifyInvitationCode(code);
    }
    return () => { cancelled = true; };
  }, []);

  const resolveShareToken = async (token, cancelled) => {
    if (regToken) return; // already resolved
    setVerifyingCode(true);
    try {
      const res = await fetch(`${API_URL}/auth/resolve-token?token=${token}`);
      const data = await res.json();
      if (cancelled) return; // component unmounted (StrictMode)
      if (data.success && data.valid) {
        setInvitationStatus({ valid: true, plantId: data.plantId, role: data.role });
        if (data.regToken) setRegToken(data.regToken);
        // Clean the token from URL so it can't be shared/bookmarked
        window.history.replaceState({}, '', window.location.pathname);
      } else {
        setInvitationStatus({ valid: false, message: data.message || 'Invalid link' });
      }
    } catch {
      if (!cancelled) setInvitationStatus({ valid: false, message: 'Unable to verify link' });
    } finally { if (!cancelled) setVerifyingCode(false); }
  };

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const verifyInvitationCode = async (code) => {
    if (!code || code.length < 8) { setInvitationStatus(null); return; }
    setVerifyingCode(true);
    try {
      const res = await fetch(`${API_URL}/auth/verify-invitation?code=${code}`);
      const data = await res.json();
      setInvitationStatus(data.success && data.valid
        ? { valid: true, plantId: data.plantId, role: data.role }
        : { valid: false, message: data.message || 'Invalid code' });
    } catch {
      setInvitationStatus({ valid: false, message: 'Unable to verify code' });
    } finally { setVerifyingCode(false); }
  };

  const handleCodeChange = (e) => {
    const code = e.target.value.toUpperCase();
    setForm({ ...form, invitationCode: code });
    code.length >= 8 ? verifyInvitationCode(code) : setInvitationStatus(null);
  };

  const handleSetup = async () => {
    setError(''); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/auth/bootstrap-admin`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email, password: form.password, name: form.name })
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      authService.token = data.token; authService.user = data.user;
      onAuth();
    } catch (err) { setError(err.message || 'Setup failed'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    if (mode === 'setup') return handleSetup();
    setError(''); setLoading(true);
    try {
      const url = mode === 'login' ? `${API_URL}/auth/login` : `${API_URL}/auth/register`;
      const payload = { ...form };
      // If we have a regToken from a share link, send it instead of the code
      if (mode === 'register' && regToken) payload.regToken = regToken;
      const res = await fetch(url, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!data.success) {
        if (data.requiresApproval) {
          setError('Account created. Awaiting admin approval.');
          setTimeout(() => { setMode('login'); setForm({ email: form.email, password: '', name: '', invitationCode: '' }); setError(''); }, 3000);
          return;
        }
        throw new Error(data.message);
      }
      if (mode === 'register' && data.requiresApproval) {
        setError('Account created. Awaiting admin approval.');
        setTimeout(() => { setMode('login'); setForm({ email: form.email, password: '', name: '', invitationCode: '' }); setError(''); }, 3000);
        return;
      }
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      authService.token = data.token; authService.user = data.user;
      onAuth();
    } catch (err) { setError(err.message || 'Something went wrong'); }
    finally { setLoading(false); }
  };

  const onKey = (fn) => (e) => { if (e.key === 'Enter') fn(); };
  const isDisabled = loading || (mode === 'register' && !invitationStatus?.valid);

  const renderStatus = () => {
    if (!invitationStatus) return null;
    if (verifyingCode) return <div className="m-status"><AlertCircle size={14} /> Verifying...</div>;
    if (invitationStatus.valid) return (
      <div className="m-status m-status-ok"><CheckCircle size={14} /> Valid &mdash; {invitationStatus.plantId} &bull; {invitationStatus.role}</div>
    );
    return <div className="m-status m-status-err"><XCircle size={14} /> {invitationStatus.message}</div>;
  };

  return (
    <>
      <style>{css}</style>
      <ParticleMesh />
      <div className={`m-root ${mounted ? 'm-in' : ''}`}>
        {/* Ambient glow */}
        <div className="m-glow" />

        {/* Nav */}
        <header className="m-nav">
          <div className="m-nav-left">
            <div className="m-nav-logo-wrap">
              <img src="/vite.svg" alt="" className="m-nav-logo" />
            </div>
            <span className="m-nav-name">Miralys</span>
          </div>
          <span className="m-nav-ver">v{APP_VERSION}</span>
        </header>

        {/* Content */}
        <main className="m-main" style={{ flexDirection: isMobile ? 'column' : 'row' }}>

          {/* Left — Brand */}
          <div className="m-brand" style={{
            display: isMobile ? 'none' : 'flex'
          }}>
            <div className="m-brand-inner">
              <p className="m-brand-tag">Industrial Intelligence Platform</p>
              <h1 className="m-brand-h1">
                Smart Manufacturing,{' '}
                <span className="m-brand-accent">Reimagined.</span>
              </h1>
              <p className="m-brand-desc">
                Real-time monitoring, AI-powered analytics, & seamless plant
                operations — unified in one platform.
              </p>
              <div className="m-brand-features">
                {[
                  { icon: Zap, t: 'Industrial Dashboards' },
                  { icon: Shield, t: 'Enterprise-grade security' },
                  { icon: Cpu, t: 'AI-driven insights' }
                ].map((f, i) => (
                  <div key={i} className="m-brand-feat">
                    <div className="m-brand-feat-icon"><f.icon size={15} /></div>
                    <span>{f.t}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right — Card */}
          <div className="m-card-area">
            <div className="m-card">

              {checkingSetup ? (
                <div className="m-loading">
                  <div className="m-spinner" />
                  <p>Connecting...</p>
                </div>

              ) : mode === 'setup' ? (
                <>
                  <div className="m-card-head">
                    <h2>First-Time Setup</h2>
                    <p>Create your admin account to get started.</p>
                  </div>
                  {error && <div className="m-error">{error}</div>}
                  <div className="m-fields">
                    <label className="m-label">Full Name</label>
                    <div className="m-input-row">
                      <User size={16} className="m-input-icon" />
                      <input name="name" placeholder="John Doe" value={form.name}
                        onChange={handleChange} onKeyDown={onKey(handleSetup)} spellCheck="false" />
                    </div>
                    <label className="m-label">Email or Username</label>
                    <div className="m-input-row">
                      <Mail size={16} className="m-input-icon" />
                      <input name="email" type="text" placeholder="you@company.com"
                        value={form.email} onChange={handleChange} onKeyDown={onKey(handleSetup)}
                        spellCheck="false" autoComplete="email" />
                    </div>
                    <label className="m-label">Password</label>
                    <div className="m-input-row">
                      <Lock size={16} className="m-input-icon" />
                      <input name="password" type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••" value={form.password}
                        onChange={handleChange} onKeyDown={onKey(handleSetup)} />
                      <button className="m-eye" onClick={() => setShowPassword(!showPassword)} type="button">
                        {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                  </div>
                  <button className="m-btn-primary" onClick={handleSetup} disabled={loading}>
                    {loading ? <div className="m-btn-spin" /> : null}
                    {loading ? 'Creating...' : 'Create Account'}
                    {!loading && <ArrowRight size={16} />}
                  </button>
                </>

              ) : (
                <>
                  <div className="m-card-head">
                    <h2>{mode === 'login' ? 'Sign in' : 'Create account'}</h2>
                    <p>{mode === 'login' ? 'Welcome back. Enter your credentials.' : 'Register with your invitation code.'}</p>
                  </div>

                  {error && <div className="m-error">{error}</div>}

                  <div className="m-fields">
                    {mode === 'register' && (
                      <>
                        {!regToken && (
                          <>
                            <label className="m-label">Invitation Code</label>
                            <div className="m-input-row">
                              <Ticket size={16} className="m-input-icon" />
                              <input name="invitationCode" placeholder="XXXX-XXXX-XXXX-XXXX"
                                value={form.invitationCode} onChange={handleCodeChange}
                                onKeyDown={onKey(handleSubmit)} spellCheck="false"
                                style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }} />
                            </div>
                          </>
                        )}
                        {renderStatus()}

                        <label className="m-label">Full Name</label>
                        <div className="m-input-row">
                          <User size={16} className="m-input-icon" />
                          <input name="name" placeholder="John Doe" value={form.name}
                            onChange={handleChange} onKeyDown={onKey(handleSubmit)} spellCheck="false" />
                        </div>
                      </>
                    )}

                    <label className="m-label">{mode === 'login' ? 'Email or Username' : 'Email'}</label>
                    <div className="m-input-row">
                      <Mail size={16} className="m-input-icon" />
                      <input name="email" type={mode === 'login' ? 'text' : 'email'}
                        placeholder={mode === 'login' ? 'you@company.com' : 'you@company.com'}
                        value={form.email} onChange={handleChange} onKeyDown={onKey(handleSubmit)}
                        spellCheck="false" autoComplete="email" />
                    </div>

                    <label className="m-label">Password</label>
                    <div className="m-input-row">
                      <Lock size={16} className="m-input-icon" />
                      <input name="password" type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••" value={form.password}
                        onChange={handleChange} onKeyDown={onKey(handleSubmit)} />
                      <button className="m-eye" onClick={() => setShowPassword(!showPassword)} type="button">
                        {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                  </div>

                  <button className="m-btn-primary" onClick={handleSubmit} disabled={isDisabled}>
                    {loading ? <div className="m-btn-spin" /> : null}
                    {loading ? 'Signing in...' : mode === 'login' ? 'Continue' : 'Create Account'}
                    {!loading && <ArrowRight size={16} />}
                  </button>

                  <div className="m-switch">
                    <span className="m-switch-text">
                      {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}
                    </span>
                    <button className="m-switch-btn" onClick={() => {
                      setMode(mode === 'login' ? 'register' : 'login');
                      setError(''); setInvitationStatus(null);
                    }}>
                      {mode === 'login' ? 'Sign up' : 'Sign in'}
                    </button>
                  </div>

                  {mode === 'register' && (
                    <p className="m-help">Need an invitation code? Contact your administrator.</p>
                  )}
                </>
              )}
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="m-footer">
          <span>&copy; {new Date().getFullYear()} Miralys</span>
        </footer>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   CSS
   ═══════════════════════════════════════════════════════════ */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Inter:opsz,wght@14..32,300;14..32,400;14..32,500;14..32,600;14..32,700;14..32,800&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --bg:        #09090b;
  --surface:   #111113;
  --surface-2: #18181b;
  --border:    rgba(255,255,255,0.06);
  --border-h:  rgba(255,255,255,0.12);
  --text-1:    #fafafa;
  --text-2:    #a1a1aa;
  --text-3:    #52525b;
  --accent:    #8b5cf6;
  --accent-2:  #a78bfa;
  --danger:    #ef4444;
  --success:   #22c55e;
  --radius:    8px;
  --radius-lg: 16px;
}

body {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  background: var(--bg);
  color: var(--text-1);
}

/* ═══ Root ═══ */
.m-root {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;
  opacity: 0;
  transition: opacity 0.6s ease;
}
.m-root.m-in { opacity: 1; }

/* Ambient glow */
.m-glow {
  position: absolute;
  top: -200px;
  left: 50%;
  transform: translateX(-50%);
  width: 900px;
  height: 600px;
  background: radial-gradient(ellipse at center,
    rgba(139,92,246,0.08) 0%,
    rgba(139,92,246,0.03) 40%,
    transparent 70%
  );
  pointer-events: none;
  z-index: 0;
  animation: m-glow-drift 8s ease-in-out infinite alternate;
}
@keyframes m-glow-drift {
  from { transform: translateX(-50%) translateY(0); }
  to   { transform: translateX(-50%) translateY(20px); }
}

/* ═══ Nav ═══ */
.m-nav {
  position: relative;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 40px;
  flex-shrink: 0;
  opacity: 0;
  animation: m-fade-down 0.6s ease 0.1s forwards;
}
@keyframes m-fade-down {
  from { opacity: 0; transform: translateY(-10px); }
  to   { opacity: 1; transform: translateY(0); }
}
.m-nav-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.m-nav-logo-wrap {
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: rgba(139,92,246,0.08);
  border: 1px solid rgba(139,92,246,0.12);
}
.m-nav-logo {
  width: 20px;
  height: 20px;
  object-fit: contain;
}
.m-nav-name {
  font-size: 17px;
  font-weight: 700;
  color: var(--text-1);
  letter-spacing: -0.03em;
}
.m-nav-ver {
  font-size: 12px;
  color: var(--text-3);
}

@media (max-width: 900px) {
  .m-nav { padding: 16px 20px; }
}

/* ═══ Main ═══ */
.m-main {
  flex: 1;
  display: flex;
  align-items: center;
  position: relative;
  z-index: 1;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 0 40px;
  gap: 80px;
}
@media (max-width: 900px) {
  .m-main {
    padding: 24px 20px;
    justify-content: center;
    align-items: flex-start;
  }
}
@media (max-width: 900px) {
  /* 16px inputs prevent iOS Safari from auto-zooming on focus */
  .m-input-row input { font-size: 16px; }
  .m-input-row { height: 48px; }
}

/* ═══ Brand ═══ */
.m-brand {
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
}
.m-brand-inner {
  max-width: 480px;
}
.m-brand-tag {
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--accent-2);
  margin-bottom: 20px;
  opacity: 0;
  animation: m-fade-up 0.7s ease 0.15s forwards;
}
.m-brand-h1 {
  font-size: 44px;
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1.08;
  color: var(--text-1);
  margin-bottom: 20px;
  opacity: 0;
  animation: m-fade-up 0.7s ease 0.25s forwards;
}
.m-brand-accent {
  background: linear-gradient(90deg, var(--accent-2), #22d3ee, var(--accent-2));
  background-size: 200% 100%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: m-shimmer 6s ease-in-out infinite;
}
@keyframes m-shimmer {
  0%, 100% { background-position: 0% center; }
  50% { background-position: 200% center; }
}
.m-brand-desc {
  font-size: 16px;
  line-height: 1.65;
  color: var(--text-3);
  margin-bottom: 40px;
  max-width: 400px;
  opacity: 0;
  animation: m-fade-up 0.7s ease 0.35s forwards;
}
.m-brand-features {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.m-brand-feat {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: var(--text-2);
  transition: transform 0.2s ease, color 0.2s ease;
}
.m-brand-feat:hover {
  transform: translateX(4px);
  color: var(--text-1);
}
.m-brand-feat:nth-child(1) { opacity: 0; animation: m-fade-up 0.5s ease 0.5s forwards; }
.m-brand-feat:nth-child(2) { opacity: 0; animation: m-fade-up 0.5s ease 0.6s forwards; }
.m-brand-feat:nth-child(3) { opacity: 0; animation: m-fade-up 0.5s ease 0.7s forwards; }
.m-brand-feat-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: rgba(139,92,246,0.08);
  color: var(--accent-2);
  flex-shrink: 0;
  transition: background 0.2s ease;
}
.m-brand-feat:hover .m-brand-feat-icon {
  background: rgba(139,92,246,0.14);
}

@keyframes m-fade-up {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ═══ Card ═══ */
.m-card-area {
  flex-shrink: 0;
  width: 100%;
  max-width: 400px;
  opacity: 0;
  animation: m-fade-up 0.7s ease 0.3s forwards;
}
.m-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 40px;
  transition: border-color 0.4s ease, box-shadow 0.4s ease;
}
.m-card:hover {
  border-color: rgba(139,92,246,0.1);
  box-shadow: 0 0 80px rgba(139,92,246,0.04);
}
@media (max-width: 900px) {
  .m-card-area { max-width: 420px; }
  .m-card { padding: 28px; }
}

.m-card-head {
  margin-bottom: 28px;
}
.m-card-head h2 {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--text-1);
  margin-bottom: 6px;
}
.m-card-head p {
  font-size: 14px;
  color: var(--text-3);
  line-height: 1.5;
}

/* ═══ Error ═══ */
.m-error {
  background: rgba(239,68,68,0.08);
  border: 1px solid rgba(239,68,68,0.15);
  padding: 12px 14px;
  border-radius: var(--radius);
  margin-bottom: 20px;
  font-size: 13px;
  color: #fca5a5;
  line-height: 1.5;
  animation: m-shake 0.35s ease;
}
@keyframes m-shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  50% { transform: translateX(5px); }
  75% { transform: translateX(-3px); }
}

/* ═══ Fields ═══ */
.m-fields {
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-bottom: 24px;
}
.m-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-2);
  margin-bottom: 6px;
  margin-top: 16px;
}
.m-label:first-child {
  margin-top: 0;
}
.m-input-row {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 0 14px;
  height: 44px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  position: relative;
}
.m-input-row::after {
  content: '';
  position: absolute;
  pointer-events: none;
  bottom: -1px;
  left: 50%; right: 50%;
  height: 2px;
  background: linear-gradient(90deg, var(--accent), #22d3ee);
  border-radius: 0 0 var(--radius) var(--radius);
  transition: left 0.3s cubic-bezier(0.16,1,0.3,1), right 0.3s cubic-bezier(0.16,1,0.3,1);
}
.m-input-row:focus-within {
  border-color: var(--border-h);
  box-shadow: 0 0 0 3px rgba(139,92,246,0.06);
}
.m-input-row:focus-within::after {
  left: 0; right: 0;
}
.m-input-icon {
  color: var(--text-3);
  flex-shrink: 0;
  transition: color 0.15s ease;
}
.m-input-row:focus-within .m-input-icon {
  color: var(--text-2);
}
.m-input-row input {
  flex: 1;
  background: none;
  border: none;
  outline: none;
  color: var(--text-1);
  font-size: 14px;
  font-family: inherit;
  height: 100%;
  min-width: 0;
}
.m-input-row input::placeholder {
  color: var(--text-3);
}

.m-eye {
  background: none;
  border: none;
  color: var(--text-3);
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  transition: color 0.15s ease;
  border-radius: 4px;
}
.m-eye:hover { color: var(--text-2); }

/* ═══ Status ═══ */
.m-status {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: var(--radius);
  font-size: 12px;
  color: var(--text-3);
  background: rgba(255,255,255,0.02);
  border: 1px solid var(--border);
  margin-top: 8px;
}
.m-status-ok {
  background: rgba(34,197,94,0.06);
  border-color: rgba(34,197,94,0.12);
  color: #86efac;
}
.m-status-err {
  background: rgba(239,68,68,0.06);
  border-color: rgba(239,68,68,0.12);
  color: #fca5a5;
}

/* ═══ Button ═══ */
.m-btn-primary {
  width: 100%;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--text-1);
  color: var(--bg);
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: var(--radius);
  cursor: pointer;
  transition: background 0.2s ease, opacity 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
  letter-spacing: -0.01em;
  position: relative;
  overflow: hidden;
}
.m-btn-primary::after {
  content: '';
  position: absolute;
  top: 0; left: -100%; width: 60%; height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
  transition: left 0.5s ease;
}
.m-btn-primary:hover:not(:disabled)::after {
  left: 120%;
}
.m-btn-primary:hover:not(:disabled) {
  background: #e4e4e7;
  box-shadow: 0 4px 24px rgba(255,255,255,0.08);
}
.m-btn-primary:active:not(:disabled) {
  transform: scale(0.98);
  box-shadow: none;
}
.m-btn-primary:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.m-btn-spin {
  width: 15px; height: 15px;
  border: 2px solid rgba(0,0,0,0.15);
  border-top-color: var(--bg);
  border-radius: 50%;
  animation: m-spin 0.6s linear infinite;
}
@keyframes m-spin { to { transform: rotate(360deg); } }

/* ═══ Switch ═══ */
.m-switch {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 20px;
}
.m-switch-text {
  font-size: 13px;
  color: var(--text-3);
}
.m-switch-btn {
  background: none;
  border: none;
  color: var(--text-1);
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  text-decoration-color: rgba(255,255,255,0.2);
  text-underline-offset: 3px;
  transition: text-decoration-color 0.15s ease;
}
.m-switch-btn:hover {
  text-decoration-color: rgba(255,255,255,0.6);
  color: var(--accent-2);
}

.m-help {
  text-align: center;
  font-size: 12px;
  color: var(--text-3);
  margin-top: 14px;
}

/* ═══ Loading ═══ */
.m-loading {
  text-align: center;
  padding: 48px 0;
}
.m-loading p {
  color: var(--text-3);
  font-size: 13px;
  margin-top: 16px;
}
.m-spinner {
  width: 24px; height: 24px;
  border: 2px solid var(--border);
  border-top-color: var(--text-2);
  border-radius: 50%;
  margin: 0 auto;
  animation: m-spin 0.7s linear infinite;
}

/* ═══ Footer ═══ */
.m-footer {
  position: relative;
  z-index: 1;
  text-align: center;
  padding: 20px;
  font-size: 12px;
  color: var(--text-3);
  flex-shrink: 0;
  opacity: 0;
  animation: m-fade-up 0.5s ease 0.8s forwards;
}

/* ═══ Selection ═══ */
::selection {
  background: rgba(139,92,246,0.25);
  color: var(--text-1);
}

/* ═══ Scrollbar ═══ */
::-webkit-scrollbar { width: 6px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.06); border-radius: 3px; }

/* ═══ Select ═══ */
select {
  background: transparent;
  color: var(--text-1);
  border: none;
  outline: none;
  width: 100%;
  font-size: 14px;
  appearance: none;
}
option {
  background: var(--surface);
  color: var(--text-1);
}

/* Touch */
@media (hover: none) {
  .m-btn-primary:hover { background: var(--text-1); }
}
`;
